import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Chip,
  TextField,
  Autocomplete,
  CircularProgress,
  Alert,
  Popover,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/system";
import { Card } from "@mui/material";
import CustomButton from "components/CustomButton";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import Tooltip from "@mui/material/Tooltip";

const CustomChip = styled(Chip)(({ theme }) => ({
  "& .MuiChip-label": {
    display: "flex",
    alignItems: "center",
  },
  "& .MuiIconButton-root": {
    marginLeft: theme.spacing(1),
  },
}));

const SearchCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: "8px",
  marginBottom: theme.spacing(2),
  height: "auto",
  minHeight: "300px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
}));

const TraditionalSearch = ({
  columns,
  updateCriteria,
  handleTraditionalSearch,
  isLoading,
  error,
  setError,
}) => {
  const [criteria, setCriteria] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedChip, setSelectedChip] = useState(null);
  const [column, setColumn] = useState("");
  const [value, setValue] = useState("");

  const handleAddCriteria = () => {
    const newCriterion = { id: Date.now(), column: "", value: "" };
    setCriteria([...criteria, newCriterion]);
    updateCriteria([...criteria, newCriterion]);
  };

  const handleChipClick = (chip) => (event) => {
    setSelectedChip(chip);
    setAnchorEl(event.currentTarget);
    setColumn(chip.column);
    setValue(chip.value);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setSelectedChip(null);
    setColumn("");
    setValue("");
  };

  const handleSaveCriterion = () => {
    const updatedCriteria = criteria.map((chip) => {
      if (chip.id === selectedChip.id) {
        return { ...chip, column, value };
      }
      return chip;
    });
    setCriteria(updatedCriteria);
    updateCriteria(updatedCriteria);
    handlePopoverClose();
  };

  const handleDeleteCriterion = (chipId) => {
    const updatedCriteria = criteria.filter((chip) => chip.id !== chipId);
    setCriteria(updatedCriteria);
    updateCriteria(updatedCriteria);
  };
  return (
    <SearchCard>
      <Typography variant="h6" gutterBottom>
        Traditional Search
      </Typography>
      <Box minHeight="200px" width="100%">
        <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
          {criteria.map((chip) => (
            <CustomChip
              key={chip.id}
              label={`${chip.column}: ${chip.value}`}
              onClick={handleChipClick(chip)}
              onDelete={() => handleDeleteCriterion(chip.id)}
              color="primary"
              deleteIcon={<CloseIcon />}
              icon={<EditIcon />}
            />
          ))}
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center">
            <IconButton color="primary" onClick={handleAddCriteria} size="large">
              <AddIcon fontSize="large" />
            </IconButton>
            <Typography variant="body1" sx={{ ml: 1 }}>
              Add Criteria
            </Typography>
          </Box>
        </Box>
        <CustomButton
          variant="contained"
          color="secondary"
          onClick={handleTraditionalSearch}
          disabled={isLoading}
          size="medium"
          sx={{ width: "150px" }}
        >
          {isLoading ? <CircularProgress size={24} /> : "Search"}
        </CustomButton>
      </Box>
      {error && (
        <Alert severity="error" onClose={() => setError(false)} sx={{ mt: 2 }}>
          Something went wrong. Please try again.
        </Alert>
      )}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        PaperProps={{
          style: {
            backgroundColor: "white",
            boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        <Box sx={{ p: 2, width: 300 }}>
          <Typography variant="subtitle1" gutterBottom>
            Edit Criterion
          </Typography>
          <Autocomplete
            options={columns}
            value={column}
            onChange={(event, newValue) => setColumn(newValue)}
            renderInput={(params) => (
              <TextField {...params} label="Column" variant="outlined" fullWidth />
            )}
            sx={{ marginBottom: 2 }}
          />
          <TextField
            label="Value"
            variant="outlined"
            fullWidth
            value={value}
            onChange={(event) => setValue(event.target.value)}
            sx={{ marginBottom: 2 }}
          />
          <Box display="flex" justifyContent="space-between">
            <Button variant="contained" color="primary" onClick={handleSaveCriterion}>
              Save
            </Button>
            <Button variant="contained" color="secondary" onClick={handlePopoverClose}>
              Cancel
            </Button>
          </Box>
        </Box>
      </Popover>
    </SearchCard>
  );
};
TraditionalSearch.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.string).isRequired,
  updateCriteria: PropTypes.func.isRequired,
  handleTraditionalSearch: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.bool.isRequired,
  setError: PropTypes.func.isRequired,
  selectedStore: PropTypes.string.isRequired,
  userId: PropTypes.string.isRequired,
};

export default TraditionalSearch;
